<template lang="pug">
.product-media(:class="[layout && 'pm-'+layout]")
  slot
  .custom-scroll.media-images(
    v-drag-scroll="{vertical: vertical, snap: layout !== 'hero' || mobile}"
    ref="mediaRef"
    :class="{vertical, layout}"
    @scroll.passive="scrollMediaEvent($event)"
  )
    DynamicMedia.image(
      v-for='(image, idx) in images'
      ref="imagesRef"
      :contain="layout !== 'hero' || mobile"
      :fetchPriority="(idx === 0) ? 'high' : 'auto'"
      :config="image"
      :preload="idx === 0 ? preload : undefined"
      :widths="layout === 'hero' ? [308, 414, 1024] : undefined"
      :key='idx'
      theme="absolute"
      :fade="false"
      @click='clickImage(idx)'
      @keydown="onImageKeydown($event, idx)"
      tabindex="0"
      :role="opensGallery ? 'button' : null"
      :aria-label="opensGallery ? __('Open image {n} of {total} in gallery', { n: String(idx + 1), total: String(images.length) }) : null"
    )
      .overlay-content(v-if="$slots.overlay")
        slot(name="overlay" :idx="idx" :img="image")
      .page-number(v-if="layout === 'zoomed'") {{__('Image ' + (idx + 1) + ' of ' + images.length)}}
</template>
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { allowScroll, preventScroll } from '../../../../js/body-scroll'
import { useMQ } from '../../../../js/mq'
import { useRogue } from '../../../../js/rogue'

// Return a number between 0 and 1 for what percent of this element is visible in its parents scrolling window
// Works with both vertical and horizontal scrolling directions
function elementVisiblePercent(el: HTMLElement, vertical = false): number {
  const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = el
  const parentElement = el.parentElement
  if (!parentElement) return 0
  
  const { scrollTop, scrollLeft, clientHeight, clientWidth } = parentElement

  const elStart = vertical ? offsetTop - scrollTop : offsetLeft - scrollLeft
  const elSize = vertical ? offsetHeight : offsetWidth
  const parentSize = vertical ? clientHeight : clientWidth

  // Check if totally offscreen
  if (elStart + elSize < 0 || elStart > parentSize) {
    return 0
  }

  // If the start end is offscreen, visibility = the visible portion / total size
  if (elStart < 0) {
    return (elStart + elSize) / elSize
  }

  return (elSize - (elStart + elSize - parentSize)) / elSize
}

let initialUrl: string | undefined
if (typeof window !== 'undefined') {
  initialUrl = window.location.toString()
}

interface GalleryItem {
  type?: string
  link?: string
  src?: string
  alt?: string
  width?: number
  height?: number
  overlay?: unknown
}

interface Product {
  name?: string
  sku?: string
}

interface ImageConfig {
  src?: string
  alt?: string
  width?: number
  height?: number
  autoHeight?: boolean
  bgColor?: string
  overlay?: unknown
}

const props = withDefaults(defineProps<{
  gallery?: GalleryItem[]
  layout?: string
  product?: Product
  // Active slide - can be started > 0 (e.g. if opening into Zoom mode)
  modelValue: number
}>(), {
  gallery : () => [],
  layout  : undefined,
  product : () => ({}),
})

const emit = defineEmits<{
  click: [index: number]
  'update:modelValue': [value: number]
}>()

const mq = useMQ()
const rogue = useRogue()

const active = ref(props.modelValue)
const mediaRef = ref<HTMLElement | null>(null)
const imagesRef = ref<Array<{ $el?: HTMLElement }> | null>(null)

let scrollEvent: ReturnType<typeof setTimeout> | undefined

const mobile = computed(() => {
  const mqValue = mq.value
  
  return ['mobile', 'tablet'].indexOf(mqValue) !== -1 || props.layout === 'force-mobile'
})

const vertical = computed(() => {
  // Vertical scrolling is only activated on desktop when zoomed
  return props.layout === 'zoomed' && !mobile.value
})

const images = computed<ImageConfig[]>(() => {
  return props.gallery
    .filter(g => g.type !== 'video')
    .map((g, i) => ({
      src        : g.link || g.src,
      alt        : g.alt || `Product Image ${i + 1}`,
      width      : g.width || 1600,
      height     : g.height || 900,
      autoHeight : !g.height,
      bgColor    : vertical.value ? '#ffffff' : '#f8f8f8',
      overlay    : g.overlay,
    }))
})

// We use a Preload to force the DynamicMedia component to load an image on initial load for SEO purposes
// This should only matter on the first page load - otherwise we can let the dynamicmedia component pick a width
const preload = computed(() => {
  if (initialUrl && typeof window !== 'undefined' && initialUrl !== window.location.toString()) {
    return undefined
  }

  // This is optimized for mobile
  return 768
})

watch(() => props.modelValue, (newVal, oldVal) => {
  // Watch changes coming from arrows
  if (newVal !== active.value) {
    fade(oldVal, newVal)
  }
})

onMounted(() => {
  if (props.modelValue !== 0 && mediaRef.value) {
    const child = mediaRef.value.children[props.modelValue]
    if (child) {
      child.scrollIntoView()
    }
  }
  if (props.layout === 'zoomed') {
    preventScroll()
    nextTick(() => {
      const imageComponent = imagesRef.value?.[props.modelValue]
      if (imageComponent?.$el) {
        imageComponent.$el.focus()
      }
    })
  }
})

onBeforeUnmount(() => {
  if (props.layout === 'zoomed') {
    allowScroll()
  }
  if (scrollEvent) {
    clearTimeout(scrollEvent)
  }
})

function scrollMediaEvent() {
  // Check which image is now active
  if (scrollEvent) {
    clearTimeout(scrollEvent)
  }
  scrollEvent = setTimeout(() => {
    if (!imagesRef.value || !mediaRef.value) return
    
    // Find the most visible of all images
    const activeIndex = imagesRef.value
      .map((img, index) => {
        if (!img.$el) return { index, visible: 0 }
        
        return { index, visible: elementVisiblePercent(img.$el, vertical.value) }
      })
      .sort((a, b) => b.visible - a.visible)[0].index

    if (activeIndex !== props.modelValue) {
      active.value = activeIndex
      emit('update:modelValue', activeIndex)
    }
  }, 100)
}

function fade(oldIndex: number, newIndex: number) {
  if (newIndex === active.value) {
    return
  }
  const media = mediaRef.value
  if (!media) return

  active.value = newIndex
  const target = media.children[newIndex] as HTMLElement

  if (!target) {
    return
  }

  if (vertical.value) {
    const pad = (window.innerHeight - target.clientHeight) / 2
    media.scrollTo({ top: target.offsetTop - pad, behavior: 'smooth' })

    return
  }

  if (Math.abs(newIndex - oldIndex) === 1 || props.layout === 'zoomed') {
    media.scrollTo({ left: target.offsetLeft, behavior: 'smooth' })

    return
  }

  const from = media.children[oldIndex] as HTMLElement

  if (!from) {
    return
  }

  const fadeOut = from.cloneNode(true) as HTMLElement

  fadeOut.classList.add('fade-out')
  fadeOut.style.left = `${target.offsetLeft}px`

  media.appendChild(fadeOut)
  const img = fadeOut.querySelector('img')
  if (img) {
    img.onload = () => {
      media.scrollTo({ left: target.offsetLeft })
      fadeOut.style.opacity = '0'
    }
  }
  setTimeout(() => fadeOut.remove(), 300)
}

// In the zoomed view the images are just content; everywhere else clicking one opens the gallery
const opensGallery = computed(() => props.layout !== 'zoomed')

function onImageKeydown(e: KeyboardEvent, idx: number) {
  if (opensGallery.value && (e.key === 'Enter' || e.key === ' ')) {
    e.preventDefault()
    clickImage(idx)
  }
}

function clickImage(idx: number) {
  emit('click', idx)
  if (rogue && rogue.track) {
    rogue.track.track({ type: 'custom', event: 'Gallery Image Click', data: { name: props.product?.name, sku: props.product?.sku, imageIdx: idx } })
  }
}
</script>
<style lang="scss" scoped>

.product-media {
  position: relative;
}

.image {
  scroll-snap-align: start; /* latest (Chrome 69+) */
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  display: block;
}

.image .page-number {
  position: absolute;
  right: 10px;
  bottom: 65px;
  padding: $space-xxs;
  font-size: $size-xs;
  background: #666;
  font-family: $font-condensed;
}

.media-images {
  position: relative;
  display: flex;
  scroll-snap-type: x mandatory;
  overflow-x: auto;
  overflow-y: hidden;

  .fade-out {
    position: absolute;
    top: 0;
    transition: opacity 300ms ease;
  }
}

@include not-desktop() {
  .product-media.pm-zoomed :deep(.rg-img) {
    height: 100%;
  }
}

// Hide the scrollbar when not zoomed
.product-media:not(.pm-zoomed):not(.pm-hero) {
  .media-images.custom-scroll {
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* Internet Explorer 10+ */
    padding-bottom: 0;
  }
  .media-images::-webkit-scrollbar, .media-images::-webkit-scrollbar { /* WebKit */
    width: 0 !important;
    height: 0 !important;
  }
}

// Also hide the scrollbar for mobile hero layout
@include not-desktop() {
  .product-media:not(.pm-zoomed) {
    .media-images.custom-scroll {
      scrollbar-width: none !important; /* Firefox */
      -ms-overflow-style: none !important; /* Internet Explorer 10+ */
      padding-bottom: 0;
    }
    .media-images::-webkit-scrollbar, .media-images::-webkit-scrollbar { /* WebKit */
      width: 0 !important;
      height: 0 !important;
    }
  }
}

.pm-hero {
  .media-images:not(.thumbs-gallery) {
    min-height: 205px;
  }
}

.overlay-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

@include all-desktop() {
  .pm-hero .image {
    scroll-snap-align: none;
    width: 308px;
    height: 173px;
    margin: 0 $space-s 0 0;

    :deep(img) {
      object-fit: cover;
    }
  }

  .media-images.vertical {
    overflow-x: hidden;
    overflow-y: auto;
    scroll-snap-type: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
    padding-right: $space-s;

    .image {
      margin-bottom: $space-m;
      width: 100%;
    }
  }
}

</style>
